import { useRef, useEffect, useCallback } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useGameStore } from '@/hooks/useGameState';
import { GAME_CONSTANTS } from '@/types/game';
import { Analytics } from '@/lib/analytics';

interface GameLogicProps {
  playerPosition: THREE.Vector3;
  playerQuaternion: THREE.Quaternion;
  onEatFood: () => void;
  onEatComet: () => void;
  onDeath: () => void;
  onKillSnake: (snakeId: string) => void;
}

export const GameLogic = ({ 
  playerPosition, 
  playerQuaternion, 
  onEatFood, 
  onEatComet,
  onDeath,
  onKillSnake 
}: GameLogicProps) => {
  const playerSnake = useGameStore((state) => state.playerSnake);
  const aiSnakes = useGameStore((state) => state.aiSnakes);
  const food = useGameStore((state) => state.food);
  const comets = useGameStore((state) => state.comets);
  const eatFood = useGameStore((state) => state.eatFood);
  const eatComet = useGameStore((state) => state.eatComet);
  const updateComets = useGameStore((state) => state.updateComets);
  const killSnake = useGameStore((state) => state.killSnake);
  const addSegment = useGameStore((state) => state.addSegment);
  const addSegments = useGameStore((state) => state.addSegments);
  const updatePlayerPosition = useGameStore((state) => state.updatePlayerPosition);
  const updateAISnakes = useGameStore((state) => state.updateAISnakes);
  const updateInvincibility = useGameStore((state) => state.updateInvincibility);
  
  const segmentsRef = useRef<THREE.Vector3[]>([]);
  const lastPositionRef = useRef(new THREE.Vector3());

  // Initialize segments
  useEffect(() => {
    if (playerSnake) {
      segmentsRef.current = playerSnake.segments.map(s => s.clone());
      lastPositionRef.current.copy(playerSnake.position);
    }
  }, [playerSnake?.id]);

  useFrame((state, delta) => {
    if (!playerSnake || !playerSnake.isAlive) return;

    const time = state.clock.elapsedTime;
    
    // Update player position in store
    updatePlayerPosition(playerPosition, playerQuaternion);

    // Update segment positions (follow the head)
    const distMoved = playerPosition.distanceTo(lastPositionRef.current);
    if (distMoved > GAME_CONSTANTS.SEGMENT_SPACING * 0.5) {
      // Shift segments
      for (let i = segmentsRef.current.length - 1; i > 0; i--) {
        segmentsRef.current[i].copy(segmentsRef.current[i - 1]);
      }
      if (segmentsRef.current.length > 0) {
        segmentsRef.current[0].copy(lastPositionRef.current);
      }
      lastPositionRef.current.copy(playerPosition);

      // Update store
      playerSnake.segments.forEach((seg, i) => {
        if (segmentsRef.current[i]) {
          seg.copy(segmentsRef.current[i]);
        }
      });
    }

    // Food collision detection - generous hitbox for easier eating
    const growthFactor = 1 + Math.log10(1 + playerSnake.score * 0.1) * 0.4;
    const headRadius = GAME_CONSTANTS.SNAKE_BASE_RADIUS * growthFactor;
    const eatMultiplier = 1.5; // 50% larger effective collision area
    
    for (const orb of food) {
      const dist = playerPosition.distanceTo(orb.position);
      if (dist < (headRadius + orb.radius) * eatMultiplier) {
        eatFood(orb.id);
        addSegment();
        segmentsRef.current.push(segmentsRef.current[segmentsRef.current.length - 1]?.clone() || playerPosition.clone());
        onEatFood();
        break; // Only eat one per frame
      }
    }

    // Player comet collision
    for (const comet of comets) {
      const dist = playerPosition.distanceTo(comet.position);
      if (dist < headRadius + GAME_CONSTANTS.COMET_RADIUS) {
        eatComet(comet.id, playerSnake.name);
        addSegments(GAME_CONSTANTS.COMET_VALUE);
        for (let i = 0; i < GAME_CONSTANTS.COMET_VALUE; i++) {
          segmentsRef.current.push(segmentsRef.current[segmentsRef.current.length - 1]?.clone() || playerPosition.clone());
        }
        Analytics.cometCatch(playerSnake.score, GAME_CONSTANTS.COMET_VALUE);
        onEatComet();
        break;
      }
    }

    // AI comet collision (passive - only if path intersects per spec)
    for (const aiSnake of aiSnakes) {
      if (!aiSnake.isAlive) continue;
      
      const aiGrowthFactor = 1 + Math.log10(1 + aiSnake.score * 0.1) * 0.4;
      const aiHeadRadius = GAME_CONSTANTS.SNAKE_BASE_RADIUS * aiGrowthFactor;
      
      for (const comet of comets) {
        const dist = aiSnake.position.distanceTo(comet.position);
        if (dist < aiHeadRadius + GAME_CONSTANTS.COMET_RADIUS) {
          eatComet(comet.id, aiSnake.name);
          // AI snake grows significantly - handled by eatComet
          break;
        }
      }
    }

    // Collision detection with AI snakes (player vs AI)
    if (!playerSnake.invincible) {
      for (const aiSnake of aiSnakes) {
        if (!aiSnake.isAlive || aiSnake.invincible) continue;

        // Check if player head hits AI body
        for (const segment of aiSnake.segments) {
          const dist = playerPosition.distanceTo(segment);
          const collisionDist = headRadius + GAME_CONSTANTS.SNAKE_BASE_RADIUS;
          
          if (dist < collisionDist) {
            killSnake('player', 'ai');
            onDeath();
            return;
          }
        }

        // Check if AI head hits player body
        const aiGrowthFactor = 1 + Math.log10(1 + aiSnake.score * 0.1) * 0.4;
        const aiHeadRadius = GAME_CONSTANTS.SNAKE_BASE_RADIUS * aiGrowthFactor;
        
        for (const segment of playerSnake.segments) {
          const dist = aiSnake.position.distanceTo(segment);
          const collisionDist = aiHeadRadius + GAME_CONSTANTS.SNAKE_BASE_RADIUS;
          
          if (dist < collisionDist) {
            killSnake(aiSnake.id);
            onKillSnake(aiSnake.id);
            break;
          }
        }
      }
    }

    // AI vs AI collision detection (same rules as player)
    for (let i = 0; i < aiSnakes.length; i++) {
      const attackerSnake = aiSnakes[i];
      if (!attackerSnake.isAlive || attackerSnake.invincible) continue;

      const attackerGrowthFactor = 1 + Math.log10(1 + attackerSnake.score * 0.1) * 0.4;
      const attackerHeadRadius = GAME_CONSTANTS.SNAKE_BASE_RADIUS * attackerGrowthFactor;

      for (let j = 0; j < aiSnakes.length; j++) {
        if (i === j) continue; // Don't check against self
        
        const victimSnake = aiSnakes[j];
        if (!victimSnake.isAlive || victimSnake.invincible) continue;

        // Check if attacker's head hits victim's body segments
        for (const segment of victimSnake.segments) {
          const dist = attackerSnake.position.distanceTo(segment);
          const collisionDist = attackerHeadRadius + GAME_CONSTANTS.SNAKE_BASE_RADIUS;
          
          if (dist < collisionDist) {
            // Attacker dies (ran into victim's body)
            killSnake(attackerSnake.id);
            break;
          }
        }
      }
    }

    // Update comet positions
    updateComets(delta);

    // Update AI snakes
    updateAISnakes(delta, time);

    // Update invincibility timer
    updateInvincibility(delta);
  });

  return null;
};
